'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePathname } from 'next/navigation';

export function NavigationProgress() {
  const pathname = usePathname();
  const [state, setState] = useState<'idle' | 'loading' | 'completing'>('idle');
  const prevPathname = useRef(pathname);

  const startLoading = useCallback(() => {
    setState('loading');
  }, []);

  // Detect navigation completion via pathname change
  useEffect(() => {
    if (pathname !== prevPathname.current) {
      prevPathname.current = pathname;
      if (state === 'loading') {
        setState('completing');
        const timer = setTimeout(() => setState('idle'), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [pathname, state]);

  // Detect clicks on internal links via event delegation
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      // Ignore modified clicks (new tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      // Ignore external links and target="_blank"
      if (anchor.target === '_blank') return;
      if (anchor.rel?.includes('external')) return;

      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/')) return;

      // Ignore hash-only navigation on the same page
      const [path] = href.split('#');
      if (path === prevPathname.current || (!path && href.includes('#'))) return;

      startLoading();
    }

    // Also handle back/forward navigation
    function handlePopState() {
      startLoading();
    }

    document.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('popstate', handlePopState);
    };
  }, [startLoading]);

  const isVisible = state !== 'idle';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed top-0 left-0 right-0 z-[9999] h-[3px]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="h-full bg-zibarit-verde-oscuro origin-left"
            initial={{ scaleX: 0 }}
            animate={{
              scaleX: state === 'completing' ? 1 : 0.8,
            }}
            transition={
              state === 'completing'
                ? { duration: 0.2, ease: 'easeOut' }
                : { duration: 2.5, ease: [0.1, 0.4, 0.2, 0.9] }
            }
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
